// src/components/Colegiado/HistoricoCambiosMios.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, Snackbar, Button, Chip, Stack, Divider, Alert
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

const API = process.env.REACT_APP_API_URL;
const PREMIO_ID = 1; // 👈 ID fijo del sello

function formateaFecha(v) {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : new Intl.DateTimeFormat("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  }).format(d);
}

const CAMPOS_COMPARABLES = [
  "nombre", "apellido_1", "apellido_2",
  "nif", "email", "email_2", "email_3",
  "telefono_1", "telefono_2", "telefono_3",
  "direccion", "localidad", "c_p_", "provincia", "pais",
  "sexo", "situacion", "rol", "moroso", "remesable",
  "banco", "bancos", "sucursal_Bancaria", "cuenta_Bancaria", "digito_Control",
  "iban", "bic",
  "fecha_Nacimiento", "provinciaNacimiento", "localidadNacimiento",
  "nacionalidad", "nacionalidad2",
  "lopd", "edad", "domiciliar_Recibo", "alertas_Sms",
  "estados_Colegiados_Estatica", "id_Busqueda", "fecha_1a_Colegiacion",
  "origen_Temporal", "tipos_Alta_Temporal",
];

const normStr = (v) => (v == null ? "" : String(v).trim());

function diffsEntreSnapshots(current, prev) {
  if (!prev) return [];
  const cambios = [];
  for (const campo of CAMPOS_COMPARABLES) {
    const a = normStr(current?.[campo]);
    const b = normStr(prev?.[campo]);
    if (a !== b) {
      const isPwd = campo.toLowerCase() === "password";
      cambios.push({
        campo,
        valorAnterior: isPwd ? "••••••" : b,
        valorNuevo: isPwd ? "••••••" : a,
      });
    }
  }
  return cambios;
}

// Parse robusto (ISO, DD/MM/YYYY, MM/DD/YYYY)
const parseFecha = (v) => {
  if (!v) return null;
  const d = dayjs(v, ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD"], true);
  return d.isValid() ? d : (dayjs(v).isValid() ? dayjs(v) : null);
};

export default function HistoricoCambiosMios() {
  const { token } = useAuth();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [mensaje, setMensaje] = useState("");
  const [sev, setSev] = useState("success");

  const [solicitando, setSolicitando] = useState(false);

  const [perfil, setPerfil] = useState(null);
  const [errorPerfil, setErrorPerfil] = useState("");

  const [comprobandoRecibos, setComprobandoRecibos] = useState(false);
  const [tienePendientes, setTienePendientes] = useState(false);
  const [errorRecibos, setErrorRecibos] = useState("");

  const [checkingSello, setCheckingSello] = useState(false);
  const [hasSello, setHasSello] = useState(false); // 👈 nuevo estado

  // === Cargar perfil + histórico ===
  const cargar = async () => {
    try {
      setCargando(true);
      const [perfilRes, histRes] = await Promise.all([
        axios.get(`${API}/colegiado/perfil`, { headers: authHeader }).catch(() => {
          setErrorPerfil("No se pudo cargar el perfil.");
          return { data: null };
        }),
        axios.get(`${API}/api/historico-cambios/mio`, { headers: authHeader }),
      ]);

      setPerfil(perfilRes?.data ?? null);

      const lista = (histRes.data ?? [])
        .map(h => ({
          ...h,
          tipoCambio: h?.tipoCambio ?? h?.tipo_cambio ?? "",
        }))
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

      const enriquecidos = lista.map((h, idx) => {
        const backendArray = Array.isArray(h?.cambios) ? h.cambios
          : h?.resumenCambios ? h.resumenCambios
            : h?.cambiosJson ? Object.entries(h.cambiosJson).map(([campo, v]) => ({
              campo,
              valorAnterior: v?.anterior ?? v?.old,
              valorNuevo: v?.nuevo ?? v?.new
            }))
              : null;

        if (backendArray && backendArray.length) return { ...h, cambiosArray: backendArray };

        const tipo = (h.tipoCambio || "").toLowerCase();
        const mostrarDiff =
          tipo.includes("cambio") || tipo.includes("modific") || tipo.includes("actualiz");
        if (!mostrarDiff) return { ...h, cambiosArray: [] };

        const prev = lista[idx + 1];
        const calculados = diffsEntreSnapshots(h, prev);
        return { ...h, cambiosArray: calculados };
      });

      setRows(enriquecidos);
    } catch {
      setSev("error");
      setMensaje("Error al cargar el histórico de cambios.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Código colegiado (estable) para recibos/premios
  const codigo = useMemo(() => (
    perfil?.codigo ?? perfil?.colegiado_codigo ?? perfil?.colegiadoCodigo ?? null
  ), [perfil]);

  // Última ALTA
  const ultimaAlta = useMemo(
    () => rows.find(c => (c?.tipoCambio || "").toLowerCase() === "alta"),
    [rows]
  );

  // Fecha base: SOLO fecha_alta_colegiacion
  const fechaInicio = useMemo(() => {
    const f = parseFecha(
      perfil?.fecha_alta_colegiacion ||
      perfil?.fechaAltaColegiacion
    );

    // Si no existe, como fallback usamos la última ALTA del histórico
    const base = f || parseFecha(ultimaAlta?.fecha);

    return base && base.isValid() ? base : null;
  }, [perfil, ultimaAlta]);

  const fechaAltaFmt = useMemo(
    () => (fechaInicio ? fechaInicio.format("DD/MM/YYYY") : null),
    [fechaInicio]
  );

  // BAJAS desde fecha base
  const hayBajaDesdeInicio = useMemo(() => {
    if (!fechaInicio) return false;
    const t0 = fechaInicio.valueOf();
    return rows.some(r => {
      const tipo = (r?.tipoCambio || "").toLowerCase();
      if (tipo !== "baja") return false;
      const f = parseFecha(r.fecha);
      return f && f.valueOf() >= t0;
    });
  }, [rows, fechaInicio]);

  // Cumple 5 años
  const cumple5Anios = useMemo(() => {
    if (!fechaInicio) return false;
    return dayjs().isSameOrAfter(fechaInicio.add(5, "year"));
  }, [fechaInicio]);

  // Recibos (única ejecución por código)
  useEffect(() => {
    if (!codigo) return;
    let alive = true;
    (async () => {
      try {
        setComprobandoRecibos(true);
        setErrorRecibos("");
        const url = `${API}/api/colegiados/${encodeURIComponent(codigo)}/historial`;
        const res = await axios.get(url, { headers: authHeader });
        if (!alive) return;

        const raw = res?.data;
        const recibos = Array.isArray(raw) ? raw : Array.isArray(raw?.recibos) ? raw.recibos : [];
        const estado = (r) => String(r.resultado_banco ?? r.resultadoBanco ?? "PENDIENTE").toUpperCase();

        const hayPend = recibos.some(r => {
          const e = estado(r);
          return e === "PENDIENTE" || e === "PENDIENTE_DEVUELTO";
        });

        setTienePendientes(hayPend);
      } catch (e) {
        setErrorRecibos("No se pudo comprobar si existen recibos pendientes.");
        setTienePendientes(true); // conservador
        console.error("Error comprobando recibos:", e);
      } finally {
        if (alive) setComprobandoRecibos(false);
      }
    })();
    return () => { alive = false; };
  }, [API, authHeader, codigo]);

  // 👇 NUEVO: comprobar si ya tiene el sello (premioId=1)
  useEffect(() => {
    if (!codigo) return;
    let alive = true;
    (async () => {
      try {
        setCheckingSello(true);
        // Ajusta esta ruta si tu backend expone otra distinta
        const PREMIOS_ENDPOINT = `${API}/colegiado/premios`;
        const res = await axios.get(PREMIOS_ENDPOINT, { headers: authHeader });
        if (!alive) return;
        const premios = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.items) ? res.data.items : []);
        const yaTiene = premios.some(p => Number(p?.id) === PREMIO_ID);
        setHasSello(yaTiene);
      } catch (e) {
        // Si falla la consulta de premios, no bloqueamos la UI;
        // el backend es idempotente al asignar.
        console.warn("No se pudo obtener la lista de premios del colegiado.", e);
      } finally {
        if (alive) setCheckingSello(false);
      }
    })();
    return () => { alive = false; };
  }, [API, authHeader, codigo]);

  // Elegibilidad total (añadimos que NO lo tenga ya y no esté en comprobación)
  const elegibleSello = Boolean(
    fechaInicio && cumple5Anios && !hayBajaDesdeInicio && !tienePendientes && !comprobandoRecibos && !hasSello && !checkingSello
  );

  // Métricas informativas
  const { anos, meses, dias } = useMemo(() => {
    if (!fechaInicio) return { anos: 0, meses: 0, dias: 0 };
    const diff = dayjs.duration(dayjs().diff(fechaInicio));
    return { anos: diff.years(), meses: diff.months(), dias: diff.days() };
  }, [fechaInicio]);

  const solicitarSello = async () => {
    try {
      setSolicitando(true);
      await axios.post(
        `${API}/colegiado/solicitar-sello`,
        { premioId: PREMIO_ID },
        { headers: authHeader }
      );
      setSev("success");
      setMensaje("Solicitud enviada. Se ha notificado a administración.");
      setHasSello(true);   // 👈 ya no volverá a mostrarse el botón
    } catch (e) {
      console.error("Error solicitando sello:", e, e?.response);
      const backendMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (typeof e?.response?.data === "string" ? e.response.data : null);
      setSev("error");
      setMensaje(backendMsg || "Error al enviar la solicitud.");
    } finally {
      setSolicitando(false);
    }
  };

  return (
    <Box component={Paper} p={3} mt={3}>
      <Typography variant="h5" gutterBottom>
        Mi Histórico de Cambios
      </Typography>

      {/* === BLOQUE DEL SELLO JUSTO DEBAJO DEL TÍTULO === */}
      {!cargando && (
        <Box sx={{ mb: 3 }}>
          {errorPerfil && <Alert severity="warning" sx={{ mb: 1 }}>{errorPerfil}</Alert>}
          {errorRecibos && <Alert severity="warning" sx={{ mb: 1 }}>{errorRecibos}</Alert>}

          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            Fecha considerada para el sello: <strong>{fechaAltaFmt || "—"}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Tiempo acumulado: {anos} año{anos !== 1 ? "s" : ""}, {meses} mes{meses !== 1 ? "es" : ""}, {dias} día{dias !== 1 ? "s" : ""}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            {hasSello && !checkingSello && (
              <Chip label="Sello concedido" color="success" />
            )}
            {!hasSello && !comprobandoRecibos && !checkingSello && (
              <Chip
                label={elegibleSello ? "Elegible para Sello" : "No elegible aún"}
                color={elegibleSello ? "success" : "warning"}
                variant={elegibleSello ? "filled" : "outlined"}
              />
            )}
            {hayBajaDesdeInicio && (
              <Chip label="BAJA registrada desde la fecha considerada" color="error" variant="outlined" />
            )}
            {tienePendientes && (
              <Chip label="Recibos pendientes" color="warning" variant="outlined" />
            )}
            {(comprobandoRecibos || checkingSello) && (
              <Chip label="Comprobaciones en curso…" variant="outlined" />
            )}
          </Stack>

          <Button
            variant="contained"
            color="primary"
            disabled={hasSello || !elegibleSello || solicitando}
            onClick={solicitarSello}
          >
            {hasSello ? "Sello concedido" : (solicitando ? "Enviando..." : "Solicitar sello")}
          </Button>

          {(hasSello || !elegibleSello || solicitando) && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Para solicitar el sello debe cumplir las condiciones establecidas.
            </Typography>
          )}
        </Box>
      )}

      {/* === RESTO DEL COMPONENTE === */}
      {cargando ? (
        <CircularProgress />
      ) : rows.length === 0 ? (
        <Typography>No hay cambios registrados.</Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Cambios aplicados</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((h) => {
                const tieneDetalles = Array.isArray(h.cambiosArray) && h.cambiosArray.length > 0;
                return (
                  <TableRow key={h.id}>
                    <TableCell>{formateaFecha(h.fecha)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={h.tipoCambio || "Cambio"}
                          color={
                            (h.tipoCambio || "").toLowerCase() === "baja" ? "error"
                              : (h.tipoCambio || "").toLowerCase() === "alta" ? "success"
                                : "default"
                          }
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {tieneDetalles ? (
                        <Stack spacing={0.4}>
                          {h.cambiosArray.map((c, idx) => (
                            <Typography key={idx} variant="body2">
                              <strong>{c?.campo}</strong>: “{c?.valorAnterior ?? "—"}” → “{c?.valorNuevo ?? "—"}”
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />
        </>
      )}

      <Snackbar
        open={!!mensaje}
        autoHideDuration={4000}
        onClose={() => setMensaje("")}
        message={mensaje}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{ sx: { bgcolor: sev === "error" ? "error.main" : "success.main" } }}
      />
    </Box>
  );
}
